import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, Printer, Edit, Send, Package, XCircle, Clock, CheckCircle,
  Truck, BarChart3, AlertTriangle, FileText, Calendar, MapPin, CreditCard,
  FileText2, History, Star, Download, Phone, Mail
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import PurchaseOrderDetailTabs from './PurchaseOrderDetailTabs';

const PurchaseOrderDetail = ({ poId, onBack, onReceive }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [po, setPo] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [poItems, setPoItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch purchase order data from Supabase
  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      try {
        setLoading(true);
        
        // Fetch purchase order data (no need for supplier join since supplier_name is in the same table)
        const { data: poData, error: poError } = await supabase
          .from('purchase_orders')
          .select('*')
          .eq('id', poId)
          .single();

        if (poError) {
          console.error('Error fetching purchase order:', poError);
          return;
        }

        setPo(poData);
        // Create supplier object from the supplier_name field
        setSupplier({
          id: poData.supplier_id,
          name: poData.supplier_name,
          contact_person: '未知',
          phone: '未知',
          email: '未知',
          address: '未知'
        });

        // Fetch purchase order items
        const { data: itemsData, error: itemsError } = await supabase
          .from('purchase_order_items')
          .select('*')
          .eq('purchase_order_id', poData.po_id);

        if (itemsError) {
          console.error('Error fetching purchase order items:', itemsError);
          return;
        }

        setPoItems(itemsData || []);
      } catch (error) {
        console.error('Error fetching purchase order:', error);
      } finally {
        setLoading(false);
      }
    };

    if (poId) {
      fetchPurchaseOrder();
    }
  }, [poId]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading purchase order...</div>
      </div>
    );
  }
  
  if (!po) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-medium text-gray-900">找不到採購訂單</h2>
        <p className="text-gray-500 mt-2">無法找到指定的採購訂單資料</p>
        <button 
          onClick={onBack} 
          className="mt-6 bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md transition-colors"
        >
          返回列表
        </button>
      </div>
    );
  }
  
  // Get status details
  const getStatusDetails = (status) => {
    const statusMap = {
      'draft': { name: '草稿', color: '#6B7280', icon: FileText },
      'sent': { name: '已發送', color: '#3B82F6', icon: Truck },
      'approved': { name: '已核准', color: '#10B981', icon: CheckCircle },
      'confirmed': { name: '已確認', color: '#10B981', icon: CheckCircle },
      'received': { name: '已收貨', color: '#059669', icon: Package },
      'partially_received': { name: '部分收貨', color: '#8B5CF6', icon: Package },
      'cancelled': { name: '已取消', color: '#EF4444', icon: AlertTriangle }
    };
    
    return statusMap[status] || { name: '未知', color: '#6B7280', icon: FileText };
  };
  
  const status = getStatusDetails(po.status);
  const StatusIcon = status.icon;

  // Action buttons based on status
  const renderActionButtons = () => {
    switch (po.status) {
      case 'draft':
        return (
          <>
            <button className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors">
              <Edit className="h-4 w-4" />
              <span>編輯訂單</span>
            </button>
            <button className="bg-amber-500 text-white hover:bg-amber-600 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors">
              <Clock className="h-4 w-4" />
              <span>提交審核</span>
            </button>
            <button className="border border-red-500 text-red-500 hover:bg-red-50 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors">
              <XCircle className="h-4 w-4" />
              <span>取消訂單</span>
            </button>
          </>
        );
      case 'pending_approval':
        return (
          <>
            <button className="bg-green-600 text-white hover:bg-green-700 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors">
              <CheckCircle className="h-4 w-4" />
              <span>核准訂單</span>
            </button>
            <button className="border border-red-500 text-red-500 hover:bg-red-50 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors">
              <XCircle className="h-4 w-4" />
              <span>拒絕訂單</span>
            </button>
          </>
        );
      case 'approved':
        return (
          <>
            <button className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors">
              <Send className="h-4 w-4" />
              <span>發送給供應商</span>
            </button>
            <button className="border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors">
              <Edit className="h-4 w-4 text-gray-600" />
              <span className="text-gray-600">編輯訂單</span>
            </button>
          </>
        );
      case 'sent':
      case 'partially_received':
        return (
          <>
            <button 
              onClick={onReceive}
              className="bg-indigo-600 text-white hover:bg-indigo-700 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors"
            >
              <Package className="h-4 w-4" />
              <span>收貨</span>
            </button>
            <button className="border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors">
              <Printer className="h-4 w-4 text-gray-600" />
              <span className="text-gray-600">列印訂單</span>
            </button>
          </>
        );
      case 'received':
        return (
          <>
            <button className="bg-green-600 text-white hover:bg-green-700 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors">
              <BarChart3 className="h-4 w-4" />
              <span>結案</span>
            </button>
            <button className="border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors">
              <Star className="h-4 w-4 text-gray-600" />
              <span className="text-gray-600">評價供應商</span>
            </button>
          </>
        );
      default:
        return (
          <button className="border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors">
            <Printer className="h-4 w-4 text-gray-600" />
            <span className="text-gray-600">列印訂單</span>
          </button>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <button 
            onClick={onBack} 
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ChevronLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">{po.po_number || '未知訂單號'}</h1>
            <p className="text-gray-500">建立於 {po.created_at ? new Date(po.created_at).toLocaleDateString() : '未知日期'}</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-3">
          {renderActionButtons()}
        </div>
      </div>

      {/* Status Banner */}
      <div 
        className="rounded-lg p-4 flex items-center justify-between"
        style={{ backgroundColor: status.color + '15' }}
      >
        <div className="flex items-center space-x-3">
          <div 
            className="p-2 rounded-full"
            style={{ backgroundColor: status.color + '30' }}
          >
            <StatusIcon className="h-5 w-5" style={{ color: status.color }} />
          </div>
          <div>
            <p className="font-medium" style={{ color: status.color }}>{status.name}</p>
            <p className="text-sm text-gray-500">
              {po.status === 'draft' && '此訂單尚未提交審核'}
              {po.status === 'sent' && '此訂單已發送給供應商'}
              {po.status === 'approved' && '此訂單已核准'}
              {po.status === 'confirmed' && '此訂單已確認'}
              {po.status === 'partially_received' && '部分物品已收貨'}
              {po.status === 'received' && '所有物品已收貨完成'}
              {po.status === 'cancelled' && '此訂單已取消'}
            </p>
          </div>
        </div>
        
        <div className="hidden md:block">
          <button className="border border-gray-300 hover:bg-gray-50 px-3 py-1.5 rounded-md flex items-center space-x-2 transition-colors">
            <Download className="h-4 w-4 text-gray-600" />
            <span className="text-gray-600">下載 PDF</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('details')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'details' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            訂單詳情
          </button>
          <button
            onClick={() => setActiveTab('receipts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'receipts' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            收貨記錄
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'history' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            訂單歷程
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <PurchaseOrderDetailTabs 
        activeTab={activeTab} 
        po={po} 
        supplier={supplier} 
        paymentTerm={po.payment_terms || '未知'} 
        shippingMethod={po.shipping_method || '未知'} 
        poItems={poItems}
      />
    </div>
  );
};

export default PurchaseOrderDetail;
